<!--
  Accept and decline belong to the receiving side only: this list only ever holds
  proposals targeting the active ledger, and it is the one place those two controls
  appear - a sent proposal (SentProposalsList) never carries them.
-->
<script setup lang="ts">
import type { RelationshipProposal } from '../internal/types.ts'
import { ActionButton, Card, EmptyState } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import LedgerAvatar from '@/features/relationships/components/LedgerAvatar.vue'
import RelationshipIcon from '@/features/relationships/components/RelationshipIcon.vue'

defineProps<{
  proposals: readonly RelationshipProposal[]
  ledgerName: (ledgerId: string) => string
  isBusy: (proposalId: string) => boolean
  canRespond: boolean
}>()

const emit = defineEmits<{
  accept: [proposal: RelationshipProposal]
  decline: [proposal: RelationshipProposal]
}>()

const { t } = useI18n()

function settlementLabel(settlement: RelationshipProposal['settlement']): string {
  return settlement === 'CONTRIBUTION'
    ? t('relationships.settlementContribution')
    : t('relationships.settlementReimbursement')
}
</script>

<template>
  <section class="flex flex-col gap-[var(--space-block)]">
    <h2
      class="flex items-center gap-2 text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]"
    >
      <RelationshipIcon name="inbox" />
      {{ t('relationships.receivedTitle') }}
      <span v-if="proposals.length > 0" class="text-[color:var(--color-text-muted)]"
        >({{ proposals.length }})</span
      >
    </h2>

    <EmptyState v-if="proposals.length === 0" :title="t('relationships.receivedEmpty')">
      <template #icon><RelationshipIcon name="inbox" /></template>
    </EmptyState>

    <Card v-else padding="none">
      <ul class="flex flex-col divide-y divide-[color:var(--color-border)]">
        <li
          v-for="proposal in proposals"
          :key="proposal.id"
          class="flex items-center justify-between gap-[var(--space-cell)] px-[var(--space-cell)] py-[var(--space-inline)]"
        >
          <div class="flex min-w-0 items-center gap-[var(--space-cell)]">
            <LedgerAvatar :id="proposal.proposingLedgerId" />
            <div class="flex min-w-0 flex-col">
              <p class="truncate text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)]">
                {{ t('relationships.proposalFrom', { ledger: ledgerName(proposal.proposingLedgerId) }) }}
              </p>
              <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('relationships.connectionRequest') }} · {{ settlementLabel(proposal.settlement) }}
              </p>
            </div>
          </div>
          <div v-if="canRespond" class="flex shrink-0 items-center gap-[var(--space-inline)]">
            <ActionButton variant="quiet" :loading="isBusy(proposal.id)" @click="emit('decline', proposal)">
              {{ t('relationships.decline') }}
            </ActionButton>
            <ActionButton
              variant="secondary"
              :loading="isBusy(proposal.id)"
              @click="emit('accept', proposal)"
            >
              {{ t('relationships.accept') }}
            </ActionButton>
          </div>
        </li>
      </ul>
    </Card>
  </section>
</template>
